import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import HeadsetMicOutlinedIcon from "@material-ui/icons/HeadsetMicOutlined";
import HourglassEmptyOutlinedIcon from "@material-ui/icons/HourglassEmptyOutlined";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";

import useTickets from "../../hooks/useTickets";
import openSocket from "../../services/socket-io";

import { AuthContext } from "../../context/Auth/AuthContext";

import { i18n } from "../../translate/i18n";

import Chart from "./Chart";
import TicketsTeamView from "../../components/TicketsTeamView";

const useStyles = makeStyles((theme) => ({
	container: {
		paddingTop: theme.spacing(3),
		paddingBottom: theme.spacing(4),
	},
	// ── faixa do usuário logado ──────────────────────────────
	userBanner: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2.5),
		padding: theme.spacing(3),
		marginBottom: theme.spacing(3),
		borderRadius: 16,
		border: `1px solid ${theme.custom.border}`,
		background:
			theme.palette.type === "dark"
				? "linear-gradient(135deg, rgba(5,150,105,.18) 0%, rgba(15,23,42,0) 60%)"
				: "linear-gradient(135deg, rgba(5,150,105,.12) 0%, rgba(255,255,255,0) 60%)",
	},
	bigAvatar: {
		width: 72,
		height: 72,
		fontSize: "1.6rem",
		fontWeight: 800,
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		boxShadow: "0 8px 24px rgba(5,150,105,.35)",
		[theme.breakpoints.down("xs")]: {
			width: 56,
			height: 56,
			fontSize: "1.2rem",
		},
	},
	userInfo: {
		minWidth: 0,
		flex: 1,
	},
	userHello: {
		fontSize: "0.8rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.08em",
		color: theme.palette.text.secondary,
	},
	userName: {
		fontWeight: 800,
		fontSize: "2rem",
		lineHeight: 1.15,
		letterSpacing: "-0.03em",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		[theme.breakpoints.down("xs")]: {
			fontSize: "1.4rem",
		},
	},
	userMeta: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginTop: 6,
		flexWrap: "wrap",
	},
	userEmail: {
		fontSize: "0.85rem",
		color: theme.palette.text.secondary,
	},
	// ── banner do tutorial guiado por voz ───────────────────
	"@keyframes tourIconPulse": {
		"0%": { boxShadow: "0 0 0 0 rgba(255,255,255,.45)" },
		"70%": { boxShadow: "0 0 0 12px rgba(255,255,255,0)" },
		"100%": { boxShadow: "0 0 0 0 rgba(255,255,255,0)" },
	},
	tourBanner: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2.5),
		padding: theme.spacing(2.5, 3),
		marginBottom: theme.spacing(3),
		borderRadius: 16,
		position: "relative",
		overflow: "hidden",
		color: "#fff",
		background:
			"linear-gradient(135deg, #10B981 0%, #059669 55%, #047857 100%)",
		boxShadow: "0 12px 32px rgba(5,150,105,.35)",
		"&::after": {
			content: '""',
			position: "absolute",
			right: -50,
			top: -50,
			width: 200,
			height: 200,
			borderRadius: "50%",
			background: "rgba(255,255,255,.08)",
			pointerEvents: "none",
		},
		[theme.breakpoints.down("xs")]: {
			flexWrap: "wrap",
		},
	},
	tourIcon: {
		width: 54,
		height: 54,
		borderRadius: 16,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: "rgba(255,255,255,.16)",
		animation: "$tourIconPulse 2.2s ease-out infinite",
		flexShrink: 0,
	},
	tourTitle: {
		fontWeight: 800,
		fontSize: "1.15rem",
		letterSpacing: "-0.02em",
		lineHeight: 1.2,
	},
	tourSub: {
		fontSize: "0.85rem",
		opacity: 0.92,
		marginTop: 2,
	},
	tourBtn: {
		marginLeft: "auto",
		background: "#fff",
		color: "#047857",
		fontWeight: 800,
		textTransform: "none",
		borderRadius: 12,
		padding: "10px 22px",
		whiteSpace: "nowrap",
		flexShrink: 0,
		boxShadow: "0 6px 18px rgba(2,6,23,.18)",
		"&:hover": { background: "#ECFDF5" },
		[theme.breakpoints.down("xs")]: {
			marginLeft: 0,
			width: "100%",
		},
	},
	// ── cards e painéis ──────────────────────────────────────
	panel: {
		padding: theme.spacing(3),
		display: "flex",
		flexDirection: "column",
		border: `1px solid ${theme.custom.border}`,
	},
	chartPanel: {
		height: 300,
		overflow: "hidden",
	},
	boardPanel: {
		minHeight: 420,
	},
	panelTitle: {
		fontWeight: 700,
		letterSpacing: "-0.01em",
		marginBottom: 2,
	},
	statCard: {
		padding: theme.spacing(2.5),
		display: "flex",
		alignItems: "center",
		gap: 16,
		border: `1px solid ${theme.custom.border}`,
	},
	statIcon: {
		width: 48,
		height: 48,
		borderRadius: 12,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
	},
	statLabel: {
		fontSize: "0.78rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		color: theme.palette.text.secondary,
	},
	statValue: {
		fontWeight: 800,
		fontSize: "1.8rem",
		lineHeight: 1.2,
		letterSpacing: "-0.02em",
	},
}));

const STAT_COLORS = {
	open: { bg: "rgba(5,150,105,.12)", fg: "#059669" },
	pending: { bg: "rgba(245,158,11,.12)", fg: "#F59E0B" },
	closed: { bg: "rgba(14,165,233,.12)", fg: "#0EA5E9" },
};

const StatCard = ({ label, value, icon, colorKey, tour }) => {
	const classes = useStyles();
	const colors = STAT_COLORS[colorKey];
	return (
		<Paper className={classes.statCard} elevation={0} data-tour={tour}>
			<div
				className={classes.statIcon}
				style={{ backgroundColor: colors.bg, color: colors.fg }}
			>
				{icon}
			</div>
			<div>
				<Typography className={classes.statLabel}>{label}</Typography>
				<Typography className={classes.statValue}>{value}</Typography>
			</div>
		</Paper>
	);
};

const Dashboard = () => {
	const classes = useStyles();

	const { user } = useContext(AuthContext);
	// memoizado: um array novo a cada render fazia o quadro de equipe refazer a
	// busca e reabrir o socket a cada atualizacao do Dashboard.
	const userQueueIds = useMemo(
		() =>
			user.queues && user.queues.length > 0
				? user.queues.map((q) => q.id)
				: [],
		[user.queues]
	);

	// qualquer evento de ticket/mensagem dispara um refetch (debounced)
	// dos cards e do grafico — dashboard sempre em tempo real
	const [refreshKey, setRefreshKey] = useState(0);
	const refreshTimer = useRef(null);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => {
			socket.emit("joinTickets", "open");
			socket.emit("joinTickets", "pending");
			socket.emit("joinTickets", "closed");
		});

		const scheduleRefresh = () => {
			clearTimeout(refreshTimer.current);
			refreshTimer.current = setTimeout(
				() => setRefreshKey((prev) => prev + 1),
				500
			);
		};

		socket.on("ticket", scheduleRefresh);
		socket.on("appMessage", scheduleRefresh);

		return () => {
			clearTimeout(refreshTimer.current);
			socket.disconnect();
		};
	}, []);

	const GetTickets = (status, showAll, withUnreadMessages) => {
		const { count } = useTickets({
			status: status,
			showAll: showAll,
			withUnreadMessages: withUnreadMessages,
			queueIds: JSON.stringify(userQueueIds),
			refreshKey,
		});
		return count;
	};

	const initials = (user?.name || "?")
		.split(" ")
		.map((n) => n[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();

	return (
		<div>
			<Container maxWidth="lg" className={classes.container}>
				{/* usuário logado em destaque */}
				<Paper className={classes.userBanner} elevation={0}>
					<Avatar className={classes.bigAvatar}>{initials}</Avatar>
					<div className={classes.userInfo}>
						<Typography className={classes.userHello}>
							{i18n.t("dashboard.loggedAs")}
						</Typography>
						<Typography className={classes.userName}>
							{user?.name || ""}
						</Typography>
						<div className={classes.userMeta}>
							<Chip
								size="small"
								color="primary"
								label={
									user?.profile === "admin"
										? i18n.t("dashboard.roleAdmin")
										: i18n.t("dashboard.roleUser")
								}
							/>
							{user?.super && (
								<Chip size="small" label={i18n.t("dashboard.roleSuper")} />
							)}
							<Typography className={classes.userEmail}>
								{user?.email || ""}
							</Typography>
						</div>
					</div>
				</Paper>

				{/* tutorial guiado por voz, sempre à mão */}
				<Paper className={classes.tourBanner} elevation={0}>
					<div className={classes.tourIcon}>
						<HeadsetMicOutlinedIcon style={{ fontSize: 30 }} />
					</div>
					<div style={{ minWidth: 0 }}>
						<Typography className={classes.tourTitle}>
							Tour guiado por voz 🎧
						</Typography>
						<Typography className={classes.tourSub}>
							Conheça o Flytec em 3 minutos — cada tela explicada passo a
							passo, com narração.
						</Typography>
					</div>
					<Button
						className={classes.tourBtn}
						onClick={() =>
							window.dispatchEvent(new Event("flytec:start-tutorial"))
						}
					>
						Começar o tour
					</Button>
				</Paper>

				<Grid container spacing={3}>
					<Grid item xs={12} sm={4}>
						<StatCard
							label={i18n.t("dashboard.messages.inAttendance.title")}
							value={GetTickets("open", "true", "false")}
							icon={<HeadsetMicOutlinedIcon />}
							colorKey="open"
							tour="dash-stat-open"
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<StatCard
							label={i18n.t("dashboard.messages.waiting.title")}
							value={GetTickets("pending", "true", "false")}
							icon={<HourglassEmptyOutlinedIcon />}
							colorKey="pending"
							tour="dash-stat-pending"
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<StatCard
							label={i18n.t("dashboard.messages.closed.title")}
							value={GetTickets("closed", "true", "false")}
							icon={<CheckCircleOutlineOutlinedIcon />}
							colorKey="closed"
							tour="dash-stat-closed"
						/>
					</Grid>

					{/* quadro de atendimentos por atendente */}
					<Grid item xs={12}>
						<Paper
							className={`${classes.panel} ${classes.boardPanel}`}
							elevation={0}
							data-tour="dash-board"
						>
							<Typography variant="h6" className={classes.panelTitle}>
								{i18n.t("dashboard.teamBoardTitle")}
							</Typography>
							<TicketsTeamView selectedQueueIds={userQueueIds} />
						</Paper>
					</Grid>

					<Grid item xs={12}>
						<Paper
							className={`${classes.panel} ${classes.chartPanel}`}
							elevation={0}
						>
							<Chart refreshKey={refreshKey} />
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	);
};

export default Dashboard;
